/**
 * Crystal Multiselect Component
 * Handles searchable multiselect functionality for crystal selection
 */
class CrystalMultiselect {
  constructor(container) {
    this.container = container;
    this.searchInput = container.querySelector('.crystal-multiselect__search');
    this.dropdown = container.querySelector('.crystal-multiselect__dropdown');
    this.options = Array.from(container.querySelectorAll('.crystal-multiselect__option'));
    this.selectedContainer = container.querySelector('.crystal-multiselect__selected');
    this.hiddenInputsContainer = container.querySelector('#crystal-hidden-inputs');
    this.errorContainer = container.querySelector('.crystal-multiselect__error');
    this.form = container.closest('form');
    
    this.selectedCrystals = [];
    this.minSelections = 2;
    
    this.init();
  }
  
  init() {
    // Search input event listeners
    this.searchInput.addEventListener('input', (e) => this.handleSearch(e));
    this.searchInput.addEventListener('focus', () => this.showDropdown());
    this.searchInput.addEventListener('blur', (e) => {
      // Delay to allow option clicks to register
      setTimeout(() => this.hideDropdown(), 200);
    });
    this.searchInput.addEventListener('keydown', (e) => this.handleKeydown(e));
    
    // Option click handlers
    this.options.forEach(option => {
      option.addEventListener('click', () => this.toggleSelection(option));
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.toggleSelection(option);
        }
      });
    });
    
    // Form submission validation
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.validateBeforeSubmit(e));
    }
    
    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.container.contains(e.target)) {
        this.hideDropdown();
      }
    });
  }
  
  handleSearch(e) {
    const searchTerm = e.target.value.toLowerCase().trim();
    this.filterOptions(searchTerm);
    this.showDropdown();
  }
  
  filterOptions(searchTerm) {
    this.options.forEach(option => {
      const text = option.textContent.toLowerCase();
      const isSelected = this.selectedCrystals.includes(option.dataset.value);
      
      if (text.includes(searchTerm) && !isSelected) {
        option.style.display = '';
        option.setAttribute('aria-selected', 'false');
      } else {
        option.style.display = 'none';
      }
    });
    
    // Update aria-expanded
    const visibleOptions = Array.from(this.options).filter(opt => opt.style.display !== 'none');
    this.searchInput.setAttribute('aria-expanded', visibleOptions.length > 0 ? 'true' : 'false');
  }
  
  showDropdown() {
    const searchTerm = this.searchInput.value.toLowerCase().trim();
    this.filterOptions(searchTerm);
    this.dropdown.hidden = false;
    this.searchInput.setAttribute('aria-expanded', 'true');
  }
  
  hideDropdown() {
    this.dropdown.hidden = true;
    this.searchInput.setAttribute('aria-expanded', 'false');
  }
  
  toggleSelection(option) {
    const value = option.dataset.value;
    const index = this.selectedCrystals.indexOf(value);
    
    if (index > -1) {
      // Deselect
      this.selectedCrystals.splice(index, 1);
      option.classList.remove('selected');
      option.setAttribute('aria-selected', 'false');
    } else {
      // Select
      this.selectedCrystals.push(value);
      option.classList.add('selected');
      option.setAttribute('aria-selected', 'true');
    }
    
    this.updateSelectedDisplay();
    this.updateHiddenInputs();
    this.clearError();
    this.searchInput.value = '';
    this.filterOptions('');
  }
  
  updateSelectedDisplay() {
    this.selectedContainer.innerHTML = '';
    
    if (this.selectedCrystals.length === 0) {
      return;
    }
    
    this.selectedCrystals.forEach(crystal => {
      const chip = document.createElement('div');
      chip.className = 'crystal-multiselect__chip';
      chip.innerHTML = `
        <span class="crystal-multiselect__chip-text">${this.escapeHtml(crystal)}</span>
        <button 
          type="button" 
          class="crystal-multiselect__chip-remove" 
          aria-label="Remove ${this.escapeHtml(crystal)}"
          data-value="${this.escapeHtml(crystal)}"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      `;
      
      const removeBtn = chip.querySelector('.crystal-multiselect__chip-remove');
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeCrystal(crystal);
      });
      
      this.selectedContainer.appendChild(chip);
    });
  }
  
  removeCrystal(crystal) {
    const index = this.selectedCrystals.indexOf(crystal);
    if (index > -1) {
      this.selectedCrystals.splice(index, 1);
      
      // Update option state
      const option = this.options.find(opt => opt.dataset.value === crystal);
      if (option) {
        option.classList.remove('selected');
        option.setAttribute('aria-selected', 'false');
      }
      
      this.updateSelectedDisplay();
      this.updateHiddenInputs();
      this.clearError();
    }
  }
  
  updateHiddenInputs() {
    const hiddenInput = document.getElementById('crystal-preferences-hidden');
    if (hiddenInput) {
      hiddenInput.value = this.selectedCrystals.length >= this.minSelections
        ? this.selectedCrystals.join(', ')
        : '';
    }
  }
  
  validateBeforeSubmit(e) {
    if (this.selectedCrystals.length < this.minSelections) {
      e.preventDefault();
      e.stopPropagation();
      this.showError(`Please select at least ${this.minSelections} crystals.`);
      // Clear any hidden inputs that might have been created
      this.updateHiddenInputs();
      // Scroll to error message
      this.errorContainer.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      this.searchInput.focus();
      return false;
    }
    // Ensure hidden inputs are created before submission
    this.updateHiddenInputs();
    this.clearError();
    return true;
  }
  
  showError(message) {
    this.errorContainer.textContent = message;
    this.errorContainer.hidden = false;
    this.errorContainer.setAttribute('role', 'alert');
    this.container.classList.add('has-error');
  }
  
  clearError() {
    this.errorContainer.hidden = true;
    this.errorContainer.textContent = '';
    this.container.classList.remove('has-error');
  }
  
  handleKeydown(e) {
    const visibleOptions = Array.from(this.options).filter(opt => opt.style.display !== 'none');
    
    if (visibleOptions.length === 0) return;
    
    let currentIndex = visibleOptions.findIndex(opt => opt === document.activeElement);
    
    switch(e.key) {
      case 'ArrowDown':
        e.preventDefault();
        currentIndex = (currentIndex + 1) % visibleOptions.length;
        visibleOptions[currentIndex].focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        currentIndex = currentIndex === -1 ? visibleOptions.length - 1 : currentIndex - 1;
        if (currentIndex < 0) currentIndex = visibleOptions.length - 1;
        visibleOptions[currentIndex].focus();
        break;
      case 'Escape':
        this.hideDropdown();
        this.searchInput.focus();
        break;
    }
  }
  
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCrystalMultiselect);
} else {
  initCrystalMultiselect();
}

function initCrystalMultiselect() {
  const container = document.getElementById('crystal-multiselect-container');
  if (container) {
    new CrystalMultiselect(container);
  }
}
